
import { Component, Mixins } from 'vue-property-decorator';
import { quotes } from '@hokify/shared-components/lib/data/quotes';
import { namespace } from 'vuex-class';
import { IAPIMarketingValues, IAPIWebinarTeaser } from '@hokify/common';
import PersonCarousel from '@hokify/shared-components/lib/components/PersonCarousel.vue';
import FAQ from '@hokify/shared-components/lib/components/FAQ.vue';
import BusinessGradientTextBlockSection from '~/components/website/sections/business/BusinessGradientTextBlockSection.vue';
import HasInteracted from '~/mixins/HasInteracted';
import LoginBase from '~/mixins/LoginBase';
import { getAllAlternateUrls, getCanonicalUrl, getSocialMediaMetaData } from '~/helpers/metadata';
import startpageFAQ from '~/data/b2b-startpage-faq.json';
import AdvertisementBanner from '@/components/website/sections/AdvertisementBanner.vue';

const cacheStore = namespace('cache');
const contentStore = namespace('b2b-content');

@Component({
	name: 'BusinessStartPage',
	components: {
		AdvertisementBanner,
		BusinessGradientTextBlockSection,
		PersonCarousel,
		FAQ,
		PopupHandler: () => import('~/components/website/PopupHandler.vue'),
		ContactFormSection: () => import('~/components/website/sections/ContactFormSection.vue'),
		ItemList: () => import('~/components/website/list/ItemList.vue'),
		LogosSectionAnimated: () => import('~/components/website/sections/LogosSectionAnimated.vue'),
		BusinessCTABanner: () => import('~/components/website/sections/business/BusinessCTABanner.vue'),
		BusinessTextBlockSection: () =>
			import('~/components/website/sections/business/BusinessTextBlockSection.vue'),
		BusinessNumbersSection: () =>
			import('~/components/website/sections/business/BusinessNumbersSection.vue'),
		BusinessHeaderSection: () =>
			import('~/components/website/sections/business/BusinessHeaderSection.vue')
	},
	layout: 'plain-company',
	scrollToTop: true,
	head() {
		const path = `${this.$store.state.host}/business`;
		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const result: any = {
			title: 'Einfach Do-it-Jobs besetzen | hokify die mobile Job-Plattform',
			link: [canonical, ...alternates],
			meta: [
				{ hid: 'robots', name: 'robots', content: 'index, follow' },
				{
					hid: 'description',
					name: 'description',
					content:
						'Mit hokify einfach und schnell Kandidaten finden über:  ► Social Media Recruiting  ► Mobile Recruiting  ► hy!Active'
				}
			]
		};
		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				undefined,
				undefined,
				this.$route.query
			)
		);
		return result;
	},
	async asyncData({ store, error }) {
		let enableHubspotPages = false;
		try {
			store.dispatch('cache/loadMarketingValues');
			enableHubspotPages = await store.dispatch(
				'feature-flag/getFeatureFlagEnabledByName',
				'enableHubspotPages'
			);
		} catch (err: any) {
			error(err);
		}

		return { enableHubspotPages };
	}
})
export default class BusinessStartPage extends Mixins(HasInteracted, LoginBase()) {
	@cacheStore.State('marketingValues') marketingValues!: IAPIMarketingValues;

	@contentStore.State('upcomingWebinarTeaser') upcomingWebinarTeaser;

	quotes = quotes;

	get latestWebinar(): IAPIWebinarTeaser | undefined {
		return this.upcomingWebinarTeaser[0];
	}

	headerItems = {
		image: 'header_startpage.png',
		fullSizeImageHeight: 634,
		fullSizeImageWidth: 950,
		headlineOne: 'Einfach effizientes',
		headlineTwo: 'Recruiting',
		subHeadline:
			'hokify ist die einfache und zeitsparende Lösung, um Kandidat:innen für Ihre Do-it-Jobs zu finden.',
		poster: false,
		buttonText: this.$store.state.topLevelDomain === 'de' ? 'Jetzt Inserat schalten' : undefined
	};

	navItems = [
		{
			title: 'Einfach zu Do-it-Jobs',
			name: 'Do-it-Jobs',
			id: '#do-it-jobs',
			image: 'do_it_jobs_icon'
		},
		{
			title: 'Einfach effizient',
			name: 'All in One <br class="hidden sm:inline"/>Recruitingprozess',
			id: '#recruiting-process',
			image: 'all_in_one_recruiting_icon'
		},
		{
			title: 'Einfach bewerben',
			name: 'hokify aus <br class="hidden sm:inline"/> Kandidat:innensicht',
			id: '#candidate-view',
			image: 'candidate_view_icon'
		},
		{
			title: 'Einfach transparent',
			name: 'BMS & Schnittstellen',
			id: '#bms',
			image: 'integrations_icon'
		}
	];

	businessDataOne = {
		image: 'do_it_jobs.png',
		imageMobile: 'do_it_jobs.png',
		alt: 'Hokify Do-it-Jobs',
		width: 479,
		height: 479
	};

	businessDataTwo = {
		image: 'all_in_one_recruiting_process.png',
		imageMobile: 'all_in_one_recruiting_process.png',
		alt: 'All-in-One Recruiting',
		width: 479,
		height: 479
	};

	businessDataThree = {
		image: 'simple_application.png',
		imageMobile: 'simple_application.png',
		alt: 'Einfache Bewerbungen',
		width: 479,
		height: 479
	};

	linkBoxes = [
		{
			icon: 'icon-people',
			headline: 'Automatisierte Prozesse &individueller Kundensupport'
		},
		{
			icon: 'icon-bullhorn',
			headline: 'Starke Reichweite dank Social Media Recruiting rocken'
		},
		{
			icon: 'icon-tasklist',
			headline: 'Übersichtliche Verwaltung &einfache BMS Anbindung'
		}
	];

	startpageFAQ = startpageFAQ;

	enableHubspotPages = false;

	bannerEndDate = new Date() > new Date('2025-07-31T21:59:59.999Z'); // Adjusted for CEST (UTC+2)

	arrangeWebinar() {
		this.$trackWebsiteEvent('arrange_hubspot_meeting', { pageElement: 'contact_us' });
		window.open(
			`https://hy.hokify.${this.$store.state.topLevelDomain}/meeting-vereinbaren`,
			'_blank'
		);
	}

	clickedCTA(element?: string, goToCaseStudies = false) {
		if (element) {
			if (goToCaseStudies) {
				this.$trackWebsiteEvent('go_to_case_studies', {
					pageElement: `business_whyhokify_${element}_block`
				});
			} else {
				this.$trackWebsiteEvent(
					element === 'go_to_webinar' ? 'go_to_webinars_detail_page' : 'go_to_pricing_page',
					{
						pageElement:
							element === 'go_to_webinar'
								? 'business_startpage_register_webinar'
								: `business_startpage_${element}_block`,
						elementContent:
							element === 'go_to_webinar' && this.latestWebinar?.articleUrl
								? this.latestWebinar.articleUrl
								: undefined
					}
				);
			}
		}
		if (element === 'go_to_webinar') {
			this.$router.push(
				this.latestWebinar?.articleUrl ? this.latestWebinar.articleUrl : '/business/webinars'
			);
		} else {
			this.$router.push(
				`/business/${goToCaseStudies ? 'recruiting/case-studies?overview=true' : 'pricing'}`
			);
		}
	}
}
